import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { socket, connectSocket } from "../../../components/socket/socket";

// Fix for Leaflet default marker icons in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
    iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const driverIcon = new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png",
    iconRetinaUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});

const pickupIcon = new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
    iconRetinaUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});

const dropoffIcon = new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
    iconRetinaUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});

function ChangeView({ center, open }) {
    const map = useMap();

    useEffect(() => {
        if (!open || !center) return;
        map.flyTo(center, map.getZoom(), { animate: true, duration: 0.6 });
    }, [center, map, open]);

    return null;
}

const MapModal = ({ open, onClose, order }) => {
    const [driverPos, setDriverPos] = useState(null);

    const pickupLat = order?.pickup_latitude ?? order?.pickup_lat;
    const pickupLng = order?.pickup_longitude ?? order?.pickup_lng;
    const dropoffLat = order?.dropoff_latitude ?? order?.dropoff_lat;
    const dropoffLng = order?.dropoff_longitude ?? order?.dropoff_lng;
    const pickupPos = pickupLat != null && pickupLng != null ? [parseFloat(pickupLat), parseFloat(pickupLng)] : null;
    const dropoffPos = dropoffLat != null && dropoffLng != null ? [parseFloat(dropoffLat), parseFloat(dropoffLng)] : null;
    const pickupAddress = order?.pickup_address;
    const dropoffAddress = order?.dropoff_address;

    useEffect(() => {
        if (!open || !order?.id) return;

        connectSocket();

        const eventName = `partnerMoved_${order.id}`;

        const handlePartnerMoved = (data) => {
            const lat = data?.latitude ?? data?.lat;
            const lng = data?.longitude ?? data?.lng;
            if (lat != null && lng != null) {
                setDriverPos([parseFloat(lat), parseFloat(lng)]);
            }
        };

        socket.on(eventName, handlePartnerMoved);

        // Initial driver position from order object if available
        if (order.driver_lat && order.driver_lng) {
            setDriverPos([parseFloat(order.driver_lat), parseFloat(order.driver_lng)]);
        }

        return () => {
            socket.off(eventName, handlePartnerMoved);
        };
    }, [open, order?.id]);

    const mapCenter = driverPos || pickupPos || [25.2854, 51.5310];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                Live Tracking - Order #{order?.order_ref}
                <IconButton onClick={onClose}>
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ height: "500px", p: 0 }}>
                <MapContainer center={mapCenter} zoom={13} style={{ height: "100%", width: "100%" }}>
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />

                    {pickupPos && (
                        <Marker position={pickupPos} icon={pickupIcon}>
                            <Popup>
                                <strong>This is the pickup location</strong>
                                {pickupAddress && <div>{pickupAddress}</div>}
                            </Popup>
                        </Marker>
                    )}

                    {dropoffPos && (
                        <Marker position={dropoffPos} icon={dropoffIcon}>
                            <Popup>
                                <strong>This is the drop off location</strong>
                                {dropoffAddress && <div>{dropoffAddress}</div>}
                            </Popup>
                        </Marker>
                    )}

                    {driverPos && (
                        <Marker position={driverPos} icon={driverIcon}>
                            <Popup>Partner Location (Live)</Popup>
                        </Marker>
                    )}

                    <ChangeView center={mapCenter} open={open} />
                </MapContainer>
            </DialogContent>
        </Dialog>
    );
};

export default MapModal;
